import { useEffect, useState, useMemo, createContext, useContext } from "react";
import { NavLink, Outlet, useLocation, Link } from "react-router-dom";
import { clubApi, fmtEur, clubName } from "../../lib/clubApi";
import { api } from "../../lib/api";

const ClubStatsCtx = createContext({ stats: null, refresh: () => {} });
export const useClubStats = () => useContext(ClubStatsCtx);

// Clean mobile-first layout inspired by user's mockup (ChatGPT design):
// Black rounded pill header + 2x2 stats grid + 2x2 tab-style actions
export default function ClubLayout() {
    const loc = useLocation();
    const [shop, setShop] = useState(null);
    useEffect(() => { api.get("/settings").then((r) => setShop(r.data)).catch(() => null); }, []);
    const [stats, setStats] = useState(null);

    const refresh = () => {
        clubApi.dashboard().then(setStats).catch(() => {});
    };

    useEffect(() => { refresh(); }, [loc.pathname]);

    const tabs = useMemo(() => [
        { to: "/club", label: "Dashboard", icon: "🏠", end: true, testid: "club-tab-dashboard" },
        { to: "/club/nuovo", label: "Nuovo socio", icon: "➕", testid: "club-tab-new" },
        { to: "/club/vendita", label: "Registra vendita", icon: "🛒", testid: "club-tab-sale" },
        { to: "/club/clienti", label: "Soci", icon: "👥", testid: "club-tab-members" },
    ], []);

    return (
        <ClubStatsCtx.Provider value={{ stats, refresh }}>
            <div className="min-h-screen bg-stone-100 text-stone-900 pb-10">
                <div className="max-w-2xl mx-auto px-4 pt-4 pb-6 space-y-4">
                    {/* Header black pill */}
                    <div className="rounded-3xl bg-stone-900 text-white p-5 shadow-md" data-testid="club-header">
                        <div className="flex items-start justify-between gap-3">
                            <div className="flex items-center gap-3 min-w-0">
                                <span className="text-3xl leading-none" role="img" aria-label="apple">🍎</span>
                                <div className="min-w-0">
                                    <h1 className="font-display font-bold text-2xl sm:text-3xl leading-tight break-words" data-testid="club-title">{clubName(shop)}</h1>
                                    <p className="text-sm text-stone-300 mt-1">Gestione soci, visite, vendite e abbonamenti</p>
                                </div>
                            </div>
                            <Link
                                to="/"
                                className="shrink-0 text-[11px] font-semibold text-stone-300 hover:text-white underline underline-offset-2"
                                data-testid="club-back-haccp"
                            >
                                ← HACCP
                            </Link>
                        </div>
                    </div>

                    {/* Stats 2x2 grid */}
                    <div className="grid grid-cols-2 gap-3">
                        <StatCard label="Soci attivi" value={stats?.active_members ?? 0} testid="stat-active" />
                        <StatCard label="Vendite questo mese" value={fmtEur(stats?.sales_this_month ?? 0)} testid="stat-sales" />
                        <StatCard label="Abbonamenti questo mese" value={fmtEur(stats?.membership_revenue_this_month ?? 0)} testid="stat-subs" />
                        <StatCard label="Visite questo mese" value={stats?.visits_this_month ?? 0} testid="stat-visits" />
                    </div>

                    {/* Tab actions 2x2 */}
                    <div className="grid grid-cols-2 gap-3" data-testid="club-tabs">
                        {tabs.map((t) => (
                            <NavLink
                                key={t.to}
                                to={t.to}
                                end={t.end}
                                data-testid={t.testid}
                                className={({ isActive }) =>
                                    `flex items-center justify-center gap-2 py-4 rounded-2xl text-base font-bold transition-all shadow-sm active:scale-[0.98] ${
                                        isActive
                                            ? "bg-stone-900 text-white shadow-md"
                                            : "bg-white text-stone-900 hover:bg-stone-50 border border-stone-200"
                                    }`
                                }
                            >
                                <span className="text-lg" aria-hidden>{t.icon}</span>
                                <span>{t.label}</span>
                            </NavLink>
                        ))}
                    </div>

                    {/* Panel below tabs */}
                    <div className="rounded-3xl bg-white p-5 shadow-sm border border-stone-200">
                        <Outlet />
                    </div>
                </div>
            </div>
        </ClubStatsCtx.Provider>
    );
}

function StatCard({ label, value, testid }) {
    return (
        <div className="rounded-2xl bg-white p-4 border border-stone-200 shadow-sm" data-testid={testid}>
            <p className="text-sm text-stone-500 leading-snug">{label}</p>
            <p className="text-2xl font-display font-bold text-stone-900 mt-1 tabular-nums truncate">{value}</p>
        </div>
    );
}
